import _ from 'lodash';
import { DateTime } from 'luxon';

import { MetaData, MetaDataTag } from 'common/response-editor/MetadataParser';
import { MarcoMetaDatum, CalendarDateObject } from 'social/publisher/Publisher.types';

export const hasLinkInMetaData = (content: MetaData[]): boolean => {
    let hasLink = false;

    content.forEach((val) => {
        const linkAttr = _.get(val, `attributes.link`);

        if (val && linkAttr) {
            hasLink = true;
        }
    });

    return hasLink;
};

export const getFirstLinkFromMetaData = (content: MetaData[]): string | undefined => {
    return _.get(
        _.find(content, (el) => {
            return _.includes(el.tags, MetaDataTag.Link);
        }),
        'attributes.link',
    );
};

export const getFirstLinkIndexFromMetaData = (content: MetaData[]): number => {
    return _.findIndex(content, (el) => {
        return _.includes(el.tags, MetaDataTag.Link);
    });
};

export const getTextFromMetaData = (content: MetaData[]): string => {
    return content.reduce((acc, cur) => {
        return acc + cur.text;
    }, '');
};

export const getHashtagTextsFromMetaData = (content: MetaData[]): string[] => {
    const hashtags: string[] = [];

    content.forEach((el) => {
        if (_.includes(el.tags, MetaDataTag.HashTag)) {
            if (el.text) {
                const hashtagWithoutHashSymbol = el.text.replace('#', ''); // backend wants hashtags without the hash sign

                hashtags.push(hashtagWithoutHashSymbol);
            }
        }
    });

    return hashtags;
};

export const hasHashtagsInMetaData = (content: MetaData[]): boolean => {
    let hasHashtags = false;

    content.forEach((el) => {
        if (_.includes(el.tags, MetaDataTag.HashTag)) {
            hasHashtags = true;
        }
    });

    return hasHashtags;
};

// all links in Twitter count as 23 chars in length
export const getTwitterContentLength = (content: MetaData[]): number => {
    let contentLength = 0;

    content.forEach((c: any) => {
        if (c.attributes && c.attributes.link) {
            contentLength += 23;
        } else {
            contentLength += c.text.length;
        }
    });

    return contentLength;
};

export const hasTemplateUrlInMetaData = (content: MetaData[]): boolean => {
    let hasTemplateUrl = false;

    content.forEach((el) => {
        if (_.includes(el.tags, MetaDataTag.TemplateUrl)) {
            hasTemplateUrl = true;
        }
    });

    return hasTemplateUrl;
};

export const getTemplateUrlMetaDataFromContent = (content: MetaData[]): MarcoMetaDatum[] => {
    const macroMetaData: MarcoMetaDatum[] = [];

    content.forEach((el) => {
        const isTemplateUrl = _.includes(el.tags, MetaDataTag.TemplateUrl);

        if (isTemplateUrl) {
            const name = _.get(el, 'attributes.label');
            const userDefinedData = _.get(el, 'attributes.userDefinedVariables') as {
                name: string;
                userDefinedValue: string;
            }[];
            let userDefinedVariables: { name: string; value: string }[] = [];

            if (userDefinedData && userDefinedData.length > 0) {
                userDefinedVariables = userDefinedData.map((el) => {
                    return {
                        name: _.get(el, 'name'),
                        value: _.get(el, 'userDefinedValue'),
                    };
                });
            }

            macroMetaData.push({
                name,
                userDefinedVariables,
            });
        }
    });

    return macroMetaData;
};
// shorten & capitalize urls, for example: http://www.nytimes.com -> NYTIMES.COM
export const formatLinkForAssetCardDisplay = (link: string): string => {
    const URL_REGEX = /^(http|https):\/\//;
    let formattedLink = link.split(URL_REGEX)[2] ? link.split(URL_REGEX)[2].toUpperCase() : link.toUpperCase();

    if (formattedLink.match(/^WWW./)) {
        formattedLink = formattedLink.split(/^WWW./).join('');
    }
    if (formattedLink.match(/\//)) {
        [formattedLink] = formattedLink.split(/\//);
    }
    return formattedLink;
};

export const formatTimezoneOffset = (offsetMinutes: number): string => {
    let operator;

    if (offsetMinutes >= 0) {
        operator = '+';
    } else {
        operator = '-';
    }

    const offsetMinutesAbs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(offsetMinutesAbs / 60));
    const formattedHours = hours.length > 1 ? hours : `0${hours}`;
    const minutes = String(offsetMinutesAbs % 60);
    const formattedMinutes = minutes.length > 1 ? minutes : `0${minutes}`;
    const formattedOffset = `${operator}${formattedHours}:${formattedMinutes}`;

    return formattedOffset;
};

export const getTimezoneFromOffset = (offsetString: string): string => {
    const matchedTimezone = getTimezoneOffsetMap().filter((val) => {
        return val.offset === offsetString;
    })[0];

    if (matchedTimezone) {
        return matchedTimezone.timezone;
    }

    return 'GMT';
};

export const isValidISOstring = (input: any): boolean => {
    if (typeof input !== 'string') {
        return false;
    }
    const dateTimeObj = DateTime.fromISO(input);

    return dateTimeObj.isValid;
};

export const getOffsetByTimezoneName = (timezoneName: string): string => {
    return formatTimezoneOffset(DateTime.local().setZone(timezoneName).offset);
};

export const getTimezoneOffsetMap = (): { timezone: string; offset: string }[] => {
    return [
        {
            timezone: 'GMT',
            offset: '+00:00',
        },
        {
            timezone: 'PT',
            offset: getOffsetByTimezoneName('America/Los_Angeles'),
        },
        {
            timezone: 'MT',
            offset: getOffsetByTimezoneName('America/Denver'),
        },
        {
            timezone: 'CT',
            offset: getOffsetByTimezoneName('America/Chicago'),
        },
        {
            timezone: 'ET',
            offset: getOffsetByTimezoneName('America/New_York'),
        },
    ];
};
export const convertDateTimeToCalendarDate = (dateTime: DateTime): CalendarDateObject => {
    return {
        year: dateTime.year,
        month: dateTime.month,
        dayOfMonth: dateTime.day,
        hour: dateTime.hour,
        minute: dateTime.minute,
        second: dateTime.second,
        timezone: formatTimezoneOffset(dateTime.offset),
    };
};
export const convertCalendarDateToDateTime = (calendarDate: CalendarDateObject): DateTime => {
    const { year, month, dayOfMonth, hour, minute, second, timezone } = calendarDate;

    return DateTime.fromObject({
        year,
        month,
        day: dayOfMonth,
        hour,
        minute,
        second,
        zone: `UTC${timezone}`,
    });
};
// Took from Sindre Sorhus's code here https://github.com/sindresorhus/escape-string-regexp/
export const escapeStringRegex = (input: string): string => {
    const matchOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g;

    return input.replace(matchOperatorsRegex, '\\$&');
};
